'use server';

import { SessionService } from '@/contexts/authentication';
import type { Character } from '../types';

/**
 * A book/story and the auto-generated (book-scoped) characters that belong to
 * it. Powers the "From stories" tab in the library page and wizard selector.
 * These characters keep their `bookId` set — we surface them for reuse without
 * mutating anything (virtual select).
 */
export interface StoryCharacterGroup {
  bookId: string;
  bookTitle: string;
  characters: Character[];
}

export async function getStoryCharacters(): Promise<StoryCharacterGroup[]> {
  const familyId = await SessionService.getFamilyId();
  if (!familyId) return [];

  const { getCharacterRepository } = await import('@/contexts/characters/repositories');
  const chars = await getCharacterRepository().listByFamily({
    familyId,
    scope: 'story',
    limit: 200,
  });
  if (!chars.length) return [];

  // Resolve book titles in one pass, index by id (best-effort — a missing book
  // just falls back to an empty title, the group still renders).
  const { getIllustrationRepository } = await import('@/contexts/illustrations/repositories');
  const books = await getIllustrationRepository().listByFamily(familyId, { limit: 500 });
  const titleById = new Map(books.map((b) => [b.id, b.title]));

  const groups = new Map<string, StoryCharacterGroup>();
  for (const c of chars) {
    if (!c.bookId) continue; // scope='story' guarantees set, guard for types
    let group = groups.get(c.bookId);
    if (!group) {
      group = { bookId: c.bookId, bookTitle: titleById.get(c.bookId) ?? '', characters: [] };
      groups.set(c.bookId, group);
    }
    group.characters.push(c as Character);
  }

  return [...groups.values()];
}
